import type { BadgeProps } from '@/components/ui/Badge'
import type { MlMatch } from '@/types'

/**
 * EP-145 A5: how each `ml_match.outcome` presents as a chip.
 *
 * Before this map every caller tested `outcome !== 'none'` and painted the rest
 * green with a "matched" label. EP-143 added four outcomes, none of which equals
 * 'none', so `ml_disagree` told the operator the ML agreed at the exact moment
 * it flagged the slip. Variant and label now both follow the outcome.
 *
 * The map is exhaustive over MlMatch['outcome'] by type, which is what forces a
 * new backend outcome to be given a presentation here instead of defaulting into
 * a misleading one. The backend half of the same guarantee is the outcome list
 * in api/spec/services/slip_verification_settlement_spec.rb.
 *
 * Label keys live in the `payments` i18n namespace under `verification.*`.
 */
export const ML_MATCH_CHIP: Record<
  MlMatch['outcome'],
  { variant: BadgeProps['variant']; labelKey: string }
> = {
  // HISTORICAL. Nothing writes these on a new settlement: the first two are the
  // EP-101 vocabulary, and EP-145 A3 deleted the rental ML-solo writer that was
  // the only source of ml_solo_verify. They stay mapped because stored JSON
  // still carries them and an unmapped outcome renders undefined.
  ref1_exact: { variant: 'success', labelKey: 'verification.ml_match_matched' },
  amount_trans_ref: { variant: 'success', labelKey: 'verification.ml_match_matched' },
  ml_solo_verify: { variant: 'info', labelKey: 'verification.ml_match_solo_verify' },
  // Written today by the rental settlement paths.
  none: { variant: 'ghost', labelKey: 'verification.ml_match_unmatched' },
  ml_agree: { variant: 'success', labelKey: 'verification.ml_match_agree' },
  ml_disagree: { variant: 'warning', labelKey: 'verification.ml_match_disagree' },
  ml_pass_rdcw_fail: { variant: 'secondary', labelKey: 'verification.ml_match_rdcw_fail' },
}
